import { useCallback, useMemo } from 'react';

import { Modules, useLoggedInUserPermissions } from '~/components/Auth';
import { useLoggedInEmployeeId } from '~/components/Auth/useLoggedInEmployeeId';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useApplicationConfig } from '~/data/applicationConfig/useApplicationConfig';
import { RelationshipType } from '~/data/search';
import { PeopleTabs } from '~/data/tabs';
import { useSelectedEmployeeDetails } from '~/hooks/employee/useSelectedEmployeeDetails';

type PropsType = {
  employeeId: number;
};

export type UseAbsencePermissionsOutput = {
  canApproveAbsence: boolean;
  canAddAbsence: boolean;
  canEditAbsences: boolean;
  canEditSelectedAbsence: (absenceId: string) => boolean;
};

export const useAbsencePermissions = (
  props: PropsType,
): UseAbsencePermissionsOutput => {
  const { employeeId } = props;

  const loggedInEmployeeId = useLoggedInEmployeeId();
  const permissions = useLoggedInUserPermissions();

  const { applicationConfig } = useApplicationConfig();
  const { isInMyUnits, hasManagerRelationshipType, isDataInSync } =
    useSelectedEmployeeDetails({
      employeeId,
    });

  const { absenceEntries } = useEmployeeAbsenceData({ employeeId });

  const { hasOwnAccess, hasFullAccess } = useMemo(() => {
    const absencesEditDisabled =
      applicationConfig?.fComConfig?.absConfig?.fComDisableAbsenceEditing;

    const peopleModuleEnabledTabs = permissions.enabledTabs[Modules.PEOPLE];
    const isAbsencesTabEnabled = peopleModuleEnabledTabs.includes(
      PeopleTabs.ABSENCES,
    );

    if (absencesEditDisabled || !isAbsencesTabEnabled) {
      return {
        hasOwnAccess: false,
        hasFullAccess: false,
      };
    }

    return {
      hasOwnAccess: employeeId === loggedInEmployeeId,
      hasFullAccess:
        isDataInSync &&
        (isInMyUnits ||
          hasManagerRelationshipType([
            RelationshipType.Direct,
            RelationshipType.Indirect,
          ])),
    };
  }, [
    employeeId,
    isDataInSync,
    isInMyUnits,
    hasManagerRelationshipType,
    loggedInEmployeeId,
    applicationConfig,
    permissions,
  ]);

  const canEditSelectedAbsence = useCallback(
    (absenceId) => {
      const absence = absenceEntries.find((entry) => entry.uid === absenceId);
      if (!absence) {
        return false;
      }

      return hasFullAccess || (hasOwnAccess && absence.status === 'PROPOSED');
    },
    [absenceEntries, hasFullAccess, hasOwnAccess],
  );

  return {
    canApproveAbsence: hasFullAccess && !hasOwnAccess,
    canAddAbsence: hasFullAccess || hasOwnAccess,
    canEditAbsences: hasFullAccess || hasOwnAccess,
    canEditSelectedAbsence,
  };
};
